'use client';

import { useState } from 'react';

import {
  ArchDiagram,
  type ArchEdge,
  type ArchNode,
} from '../shared/arch-diagram';
import { SegmentedControl } from '../shared/segmented-control';

/**
 * The latency work, drawn: the same four Workers, but the middleman relay
 * deleted, the public-internet hop replaced by an internal binding, and the
 * shared queue split into lanes.
 */

const NODES = (before: boolean): ArchNode[] => [
  { id: 'client', x: 10, y: 9, title: 'Client', sub: 'browser · app' },
  { id: 'cdn', x: 34, y: 9, title: 'Cloudflare CDN', sub: 'edge' },
  {
    id: 'api',
    x: 66,
    y: 9,
    title: 'API backend · Worker',
    sub: before ? 'also relaying completions' : 'out of the hot path',
  },
  {
    id: 'orch',
    x: 22,
    y: 44,
    title: 'Workflow orchestrator · Worker',
    sub: before ? 'one shared record' : 'per-step rows · 60s reconciler',
    emphasis: 'solid',
  },
  {
    id: 'engine',
    x: 76,
    y: 44,
    title: 'Generation engine · Worker',
    sub: 'providers · breakers',
  },
  {
    id: 'queues',
    x: 49,
    y: 66,
    title: before ? 'One shared queue' : 'Queues · 3 lanes',
    sub: before ? '5s delivery floor' : '0s floor',
    emphasis: before ? 'muted' : 'default',
  },
  { id: 'providers', x: 76, y: 88, title: 'AI providers', emphasis: 'muted' },
  {
    id: 'db',
    x: 22,
    y: 88,
    title: 'Postgres',
    sub: before ? 'steps overwrite each other' : 'steps claimed atomically',
  },
];

const EDGES = (before: boolean): ArchEdge[] => [
  {
    id: 'c-cdn',
    points: [
      [15, 9],
      [26, 9],
    ],
    pulse: true,
  },
  {
    id: 'cdn-api',
    points: [
      [42, 9],
      [53, 9],
    ],
    pulse: true,
  },
  {
    id: 'api-orch',
    points: [
      [60, 14],
      [60, 24],
      [22, 24],
      [22, 38],
    ],
    label: 'start run',
    labelAt: [41, 24],
    pulse: true,
  },
  {
    id: 'orch-engine',
    points: [
      [33, 44],
      [64, 44],
    ],
    label: before
      ? 'public internet · 3.5s/hop'
      : 'internal binding · 1.6s/hop',
    labelAt: [48.5, 49],
    dashed: before,
    pulse: !before,
  },
  {
    id: 'engine-queues',
    points: [
      [70, 49],
      [49, 49],
      [49, 60],
    ],
    pulse: true,
  },
  {
    id: 'queues-providers',
    points: [
      [57, 68],
      [76, 68],
      [76, 83],
    ],
    pulse: true,
  },
  {
    id: 'providers-engine',
    points: [
      [84, 83],
      [84, 49],
    ],
    label: 'webhooks',
    labelAt: [84, 68],
    pulse: true,
  },
  before
    ? {
        id: 'relay',
        points: [
          [76, 38],
          [76, 28],
          [70, 14],
        ],
        label: 'completion relayed via backend',
        labelAt: [62, 30],
        pulse: true,
      }
    : {
        id: 'relay-removed',
        points: [
          [76, 38],
          [76, 28],
          [70, 14],
        ],
        label: 'middleman · deleted',
        labelAt: [62, 30],
        removed: true,
      },
  ...(before
    ? []
    : [
        {
          id: 'direct',
          points: [
            [64, 41],
            [33, 41],
          ],
          label: 'direct completion · ~200ms',
          labelAt: [48.5, 36],
          pulse: true,
        } satisfies ArchEdge,
      ]),
  {
    id: 'orch-db',
    points: [
      [22, 50],
      [22, 82],
    ],
    label: before ? 'shared doc' : 'atomic claims',
    labelAt: [22, 66],
    pulse: true,
  },
];

export function LatencyArchitecture() {
  const [era, setEra] = useState<'before' | 'after'>('after');
  const before = era === 'before';

  return (
    <div className="px-4 pb-4">
      <div className="mb-3 flex justify-end">
        <SegmentedControl
          label="Architecture era"
          labelHidden
          value={era}
          onChange={setEra}
          options={[
            { value: 'before', label: 'Before' },
            { value: 'after', label: 'After' },
          ]}
        />
      </div>

      <ArchDiagram
        nodes={NODES(before)}
        edges={EDGES(before)}
        heightClass="pb-[125%] sm:pb-[60%]"
        caption={
          before
            ? 'Before: completions took the long way round through the backend, services talked over the public internet, and one queue carried everything.'
            : 'After: the relay is deleted, services talk over internal bindings, and a two-second notice never queues behind a ninety-second video job.'
        }
      />
    </div>
  );
}
